// ### App
export interface AppProps {
  loggedInUser: User;
  isCheckingAuthentication: boolean;
}

//#region InternshipApplication
enum ApplicationStatus {
  open = 0,
  accepted = 1,
  rejected = 2,
}

export interface InternshipApplication {
  id: number;
  studentId: number;
  internshipOfferId: number;
  internshipSessionId: number;
  status: ApplicationStatus;
  // the study year of the application is the study year of its session's department
  studyYearId: number;
}

export interface CombinedInternshipApplication {
  id: number;
  studentId: number;
  internshipOfferId: number;
  status: ApplicationStatus;

  internshipOfferName: string;
  numberOfHours: number;
  paid: boolean;
  organizationId: number;
  organizationName: string;
  internshipOfferDescription: string;
  isUniversityOrganization: boolean;
}

export interface WorkSafetyDocumentDetails {
  bloodGroup: string;
  birthCounty: string;
  apartment?: string;
  studies: string;
  qualification: string;
  outboundRoute: string;
  outboundTransport: string;
  outboundHours: number;
  outboundMinutes: number;
  returnRoute: string;
  returnTransport: string;
  returnHours: number;
  returnMinutes: number;
  filledInAt?: string;
}

export interface CombinedInternshipApplicationWithProcesses {
  application: CombinedInternshipApplication;
  process: InternshipProcess | null | undefined;
  processSteps: [ProcessSteps] | null;
}

export interface InternshipsApplicationsAndApplicantsOfOrganization {
  applications: InternshipApplication[];
  applicants: User[];
  internships: ReducedInternship[];
}
//#endregion

//#region InternshipOffer
export enum InternshipOfferStatus {
  active = 0,
  inactive = 1,
}

export enum InternshipOfferType {
  atUniversity = 'AtUniversity',
  researchGroup = 'ResearchGroup',
  projectBased = 'ProjectBased',
  organization = 'Organization',
}

export interface InternshipOffer {
  name: string;
  numberOfHours: number;
  numberOfPositions: number;
  paid: boolean;
  type: InternshipOfferType;
  logo: string;
  logoData: string;
  organizationId: number;
  description: string;
  internshipSessionIds: number[];
  competencyTable: CompetencyTable;
  city: string;
  street: string;
  streetNumber: string;
  status: InternshipOfferStatus;
  startTime: Date;
  endTime: Date;
  contractType: ContractType;
  otherProjectName: string | null;
  representativeName: string | null;
  representativeEmail: string | null;
}

export interface CompetencyTable {
  id?: number;
  internshipProcessId?: number | null;
  internshipOfferId?: number | null;
  competencyTableItems: CompetencyTableItem[];
}

export interface CompetencyTableItem {
  id?: number;
  nr: number;
  competency: string;
  trainingModule: string;
  workLocation: string;
  plannedActivities: string;
  observations: string;
}

export interface ReducedInternship {
  id: number;
  name: string;
  numberOfHours: number;
  paid: boolean;
  logo: string;
  logoData: string;
  organizationId: number;
  description: string;
  internshipSessions: InternshipSession[];
  status: InternshipOfferStatus;
}

export interface UpdateInternship {
  name: string;
  numberOfHours: number;
  numberOfPositions: number;
  paid: boolean;
  logo: string;
  logoData: string;
  description: string;
  internshipProcessIds: [];
  city: string;
  street: string;
  streetNumber: string;
  status: InternshipOfferStatus;
  startTime: Date;
  endTime: Date;
}

export interface DetailedInternship {
  internshipSessions: InternshipSession[];
  internshipProcessIds: number[];
  internshipApplicationIds: number[];
  id: number;
  name: string;
  numberOfHours: number;
  numberOfPositions: number;
  paid: boolean;
  logo: string;
  logoData: string;
  organizationId: number;
  description: string;
  city: string;
  street: string;
  streetNumber: string;
  status: InternshipOfferStatus;
  startTime: Date;
  endTime: Date;
}

export interface NewInternshipComponentProps {
  onNewInternShipComponentAdded(): Promise<void>;
}
export interface UpdateInternshipProps {
  id: number;
  onInternShipComponentUpdated(): void;
}
//#endregion

//#region InternshipProcess
export enum ProcessStatus {
  notStarted = 0,
  initialized = 1,
  finished = 2,
}

export interface InternshipProcess {
  id: number;
  studentId: number;
  organizationId: number;
  isUniversityOrganization: boolean;
  tutorId: number;
  workLogId: number;
  workLogDocumentUrl: string;
  workSafetyDocumentUrl?: string;
  workSafetyDocumentDetails?: WorkSafetyDocumentDetails | null;
  evaluationReportId: number;
  universitySupervisorId: number;
  deanId: number;
  rectorId: number;
  internshipApplicationId: number;
  internshipSessionId: number;
  finalGrade: number;
  conventionDocumentUrl: string;
  anexaDocumentUrl: string;
  researchAgreementId: number;
  researchAgreementDocumentUrl: string;
  processSteps: [ProcessSteps];
  currentUniversityStepName: string;
  currentOrganizationStepName: string;
  status: ProcessStatus;
  competencyTable: CompetencyTable;
  // the study year of the process is the study year of its session's department
  studyYearId: number;
}

export interface InternshipProcessWithNames extends InternshipProcess {
  studentName: string;
  studentGroup: string;
  studyLineId: number;
  studyLineName: string;
  studyLineType: StudyLineType;
  sessionName: string;
  organizationName: string;
  tutorName: string;
  supervisorName: string;
  deanName: string;
  rectorName: string;
  internshipStartDate: Date;
  internshipEndDate: Date;
}
//#endregion

//#region InternshipWorkLog
export interface InternshipWorkLogEntries {
  id: number;
  worklogId: number;
  startTime: Date;
  endTime: Date;
  description: string;
  hours: number;
  workLogStatus: WorkLogStatus;
}

export interface CombinedInternshipWorkLog {
  workLogEntries: InternshipWorkLogEntries;
}

export enum WorkLogStatus {
  OPEN = 0,
  CLOSED = 1,
  IN_REVIEW = 2,
}

//#endregion

//#region Organization
export interface Organization {
  representativeId: number;
  representativeName: string;
  name: string;
  description: string;
  city: string;
  county: string;
  street: string;
  streetNumber: string;
  phoneNumber: string;
  email: string;
  fax: string;
  fiscalCode: string;
  fiscalAccount: string;
  iban: string;
  isRomanian: boolean;
  bachelorStudents: number;
  masterStudents: number;
  language: PaperWorkLanguage;
  studyYearId: number;
}

export interface CollaborationDocument {
  id: string;
  url: string;
  collaborationType: CollaborationType;
  studyYearId: number;
  isArchived: boolean;
  isRegistered: boolean;
}

export interface newCollaborationDocument {
  studyYearId: number;
  registrationDate: string;
  registrationNumber: string;
  collaborationType: CollaborationType;
  bachelorStudentCount: number;
  masterStudentCount: number;
}

export enum CollaborationType {
  LearningAgreementForForeignOrganizations = 0,
  AnualAgreement = 1,
  CollaborationAgreement = 2,
  NotYetSpecified = 3,
}

export interface OrganizationRegistration {
  registrationNumber: string;
  registrationDate: string;
  id: number;
  organizationId: number;
  organizationName: string;
  serialNumber: string;
  processSteps: [ProcessSteps];
  status: number;
  collaborationDocument: CollaborationDocument;
  isUniversityOrganization: boolean;
}

export interface DetailedOrganizationRegistration {
  id: number;
  organizationName: string;
  organizationRepresentativeName: string;
  organizationCity: string;
  organizationEmail: string;
  registrationStatus: RegistrationStatus;
  status: StepStatus;
}

export enum RegistrationStatus {
  notRegistered = 0,
  registered = 1,
  registeringCollaborationDocument = 2,
  SignedByManager = 3,
  SignedByLegalDepartment = 4,
  SignedByRector = 5,
}

export interface ReducedOrganization {
  id: number;
  name: string;
  isUniversityOrganization: boolean;
}

export enum PaperWorkLanguage {
  Romanian = 0,
  English = 1,
}

export interface OrganizationRegistrationYear {
  registrationYearOfOrganization: number;
}

// Status of a day in the study year
export type StudyYearDayStatus =
  | 'working'
  | 'free'
  | 'exam'
  | 'retake'
  | 'outside';

// The teaching languages which share a calendar
export enum CalendarLanguages {
  RomanianEnglish = 'RomanianEnglish',
  HungarianGerman = 'HungarianGerman',
}

// The final years have their own second semester
export enum CalendarYearType {
  NonFinal = 'NonFinal',
  Final = 'Final',
}

// The academic calendar of a study year for one teaching language group and kind of years,
// dates as 'YYYY-MM-DD'
export interface StudyYearCalendar {
  languages: CalendarLanguages;
  yearType: CalendarYearType;
  firstSemesterStart: string;
  firstSemesterEnd: string;
  secondSemesterStart: string;
  secondSemesterEnd: string;
  // Every free day of the semesters: weekends, public holidays, holidays and the days
  // the rector made free, without the ones the rector made working
  freeDays: Array<string>;
  // Days of the exam and retake sessions. Every other day of the semesters is a working day,
  // the days before, between and after them (e.g. the summer holiday) are outside of the teaching period
  examDays: Array<string>;
  retakeDays: Array<string>;
  // Dates of the public holidays in the study year
  holidays: Array<string>;
}

// Dates as 'YYYY-MM-DD'
export interface StudyYear {
  id: number;
  // e.g. '2025/2026'
  name: string;
  // The teaching period: the earliest start and the latest end of its calendars
  startDate: string;
  endDate: string;
  state: StudyYearStatus;
  // One for each teaching language group and kind of years;
  // a study year saved before the calendars has none
  calendars: Array<StudyYearCalendar>;
}

// A study year built on the UI, before the backend saves it
export type NewStudyYear = Omit<StudyYear, 'id' | 'state'>;

export enum StudyYearStatus {
  current = 0,
  finished = 1,
}
//#endregion

//#region Process steps
export enum StepStatus {
  notStarted = 0,
  initialized = 1,
  updated = 2,
  finished = 3,
}

export enum StepName {
  organizationRegistrationRequested = 'OrganizationRegistrationRequested',
  allocatingTutorFromOrganization = 'AllocatingTutorFromOrganization',
  allocatingUniversitySupervisorFromUBB = 'AllocatingUniversitySupervisorFromUBB',
  signingOrganizationRegistrationDocumentByOrganization = 'SigningOrganizationRegistrationDocumentByOrganization',
  signingOrganizationRegistrationDocumentByLegalDepartment = 'SigningOrganizationRegistrationDocumentByLegalDepartment',
  signingCollaborationDocumentByRector = 'SigningCollaborationDocumentByRector',
  internshipProcessRequested = 'InternshipProcessRequested',
  workSafetyInstructionReading = 'WorkSafetyInstructionReading',
  routeStatementFilling = 'RouteStatementFilling',
  activityDeclarationFromUBBFilling = 'ActivityDeclarationFromUBBFilling',
  activityDeclarationFromOrganizationFilling = 'FillingActivityDeclarationFromOrganizationFilling',
  labourProtectionApproving = 'LabourProtectionApproving',
  conventionFillingAndSigningByStudent = 'ConventionFillingAndSigningByStudent',
  signingInternshipConventionByOrganization = 'SigningInternshipConventionByOrganization',
  signingInternshipConventionByTutor = 'SigningInternshipConventionByTutor',
  signingInternshipConventionByUniversitySupervisor = 'SigningInternshipConventionByUniversitySupervisor',
  signingInternshipConventionByRector = 'SigningInternshipConventionByRector',
  workReportFilling = 'WorkReportFilling',
  validateWorkReportByTutor = 'ValidateWorkReportByTutor',
  completingEvaluationReportByOrganization = 'CompletingEvaluationReportByOrganization',
  signingEvaluationReportByTutor = 'SigningEvaluationReportByTutor',
  gradingWorkReportByUniversitySupervisor = 'GradingWorkReportByUniversitySupervisor',
  finalizingGradeByUniversitySupervisor = 'FinalizingGradeByUniversitySupervisor',
  signingWorkReportByStudent = 'SigningWorkReportByStudent',
  signingWorkReportByTutor = 'SigningWorkReportByTutor',
  registeringConventionDocument = 'RegisteringConventionDocument',
  registeringCollaborationDocument = 'RegisteringCollaborationDocument',
  SigningInternshipConventionByUniversityProjectCoordinator = 'SigningInternshipConventionByUniversityProjectCoordinator',
}

export interface ProcessSteps {
  stepStatus: StepStatus;
  id: number;
  required: boolean;
  responsibleId: number;
  stepName: StepName;
  disabled: boolean;
}
//#endregion

//#region Roles
export enum Roles {
  Student = 1,
  CadruDidactic = 2,
  Administrator = 4,
  OrganizationRepresentative = 8,
  Tutor = 16,
  DepartmentRepresentative = 32,
  Dean = 64,
  LegalDepartment = 128,
  LaborProtection = 256,
  Rector = 512,
  UniversityCoordinator = 1024,
  UniversityProjectCoordinator = 2048,
}
//#endregion

//#region Summary table (same shape for every role)
export interface ExcelSummary {
  id: string;
  studentName: string;
  studentEmail: string;
  studyLineName: string;
  sessionName: string;
  organizationName: string;
  /** The process status name sent by the backend, e.g. "Finished". */
  status: string;
  gradeBySupervisor: number;
  gradeByTutor: number;
  // the study year of the process is the study year of its session's department
  studyYearId: number;
}

// Student
export interface Student {
  id: number;
  email: string;
  phoneNumber: string;
  city: string;
  street: string;
  streetNumber: string;
  county: string;
  cnp: string;
  idSeries: string;
  idNumber: string;
  dateOfBirth: string;
  placeOfBirth: string;
  group: string;
  nationality: string;
  internshipApplications: number[];
  internshipProcesses: number[];
  preferredStudyLineIds: number[];
  // the study year the student last checked their data for; null before the first check
  confirmedStudyYearId?: number | null;
}

// Tutor
export interface Tutor {
  id: number;
  name: string;
  roles: number;
  email: string;
}

// User
export interface User {
  id: number;
  lastName: string;
  firstName: string;
  email: string;
  phoneNumber: string;
  roles: number;
  jobTitle?: string;
  accessToken: string;
}

export interface NovuSubscriberAuthSession {
  userId: number;
  subscriberId: string;
  signature: string;
  issuedAtUtc: string;
  expiresAtUtc?: string | null;
  keyVersion?: string | null;
}

export type EmailNotificationFrequency = 'instant' | 'daily';

export interface EmailNotificationFrequencyPreference {
  frequency: EmailNotificationFrequency;
}

export interface UpdateUserDTO {
  lastName: string;
  firstName: string;
  email: string;
  phoneNumber: string;
  roles: number;
  jobTitle: string;
}

export interface InternshipSession {
  id: number;
  description: string;
  yearOfStudy: number;
  studyLineId: number;
  hours: number;
}

export enum StudyLineType {
  ComputerScience = 'ComputerScience',
  Math = 'Math',
  MathComputerScience = 'MathComputerScience',
}

// Romanian and English study lines share an academic calendar, Hungarian and German ones have their own
export enum TeachingLanguage {
  Romanian = 'Romanian',
  English = 'English',
  Hungarian = 'Hungarian',
  German = 'German',
}

export interface StudyLine {
  id: number;
  name: string;
  studyLevel: StudyLevel;
  teachingLanguage: TeachingLanguage;
  // e.g. 3 for a bachelor, 2 for a master
  numberOfYears: number;
  departmentId: number;
  departmentName: string;
  studyLineType: StudyLineType;
  internshipSessions: InternshipSession[];
}

export interface DepartmentSetup {
  id: number;
  name: string;
  studyLines: Array<StudyLine>;
}

// The departments with their study lines and sessions for one study year
export interface UniversitySetupPostDto {
  departmentsSetup: Array<DepartmentPostDto>;
}

// A new study year with the setup of the university for it, saved together
export interface StudyYearWithSetupPostDto {
  studyYear: NewStudyYear;
  setup: UniversitySetupPostDto;
}

// The departments are set up for each study year by the rector
export interface UniversityPostDto {
  name: string;
  rectorsEmails: Array<string>;
  workPlaceSafetySupervisorsEmails: Array<string>;
  legalDepartmentMembersEmails: Array<string>;
}

export interface ReducedUniversity {
  id: number;
  name: string;
}

export interface University {
  university: {
    name: string;
    rectors: Array<string>;
    workplaceSafetySupervisors: Array<string>;
    legalDepartmentMembers: Array<string>;
  };
  departments: Array<{
    departmentName: string;
    deans: Array<string>;
    projectCoordinators?: Array<string>;
  }>;
  studyLines: Array<
    Array<{
      studyLineName: string;
      studyLineLevel: StudyLevel;
      studyLineType: StudyLineType;
      teachingLanguage: TeachingLanguage;
      // empty until the rector sets it, e.g. for a study line copied from before it existed
      numberOfYears?: number;
      coordinators: Array<string>;
    }>
  >;
  sessions: Array<
    Array<{
      description: string;
      yearsOfStudy: number;
      // free text, e.g. '4 (patru)'
      courseCredit: string;
      sessionHours: number;
    }>
  >;
}

export interface UniversityMemberInviteDto {
  studyLineId: number;
  rectorEmails: Array<string>;
  legalDepartmentMemberEmails: Array<string>;
  supervisorEmails: Array<string>;
}

export interface DepartmentPostDto {
  name: string;
  studyLines: Array<StudyLinePostDto>;
  deansEmails: Array<string>;
  universityProjectCoordinatorEmails: Array<string>;
}

export interface StudyLinePostDto {
  name: string;
  studyLineType: StudyLineType;
  studyLevel: StudyLevel;
  teachingLanguage: TeachingLanguage;
  numberOfYears: number;
  sessions: Array<SessionPostDto>;
  universityCoordinatorsEmails: Array<string>;
}

export interface SessionPostDto {
  description: string;
  yearOfStudy: number;
  courseCredit: string;
  hours: number;
}

export interface LoginData {
  name: string;
  email: string;
}

export interface BaseEvaluationReport {
  evaluationDocumentUrl: string;
  studyLineType: StudyLineType;

  number: string;
  of: string;
  internshipStarted: Date | string;
  internshipCompleted: Date | string;

  otherMainActivity: string;

  internshipIncludedTraining: boolean;
  internshipIncludedSoftwareDevelopment: boolean;
  internshipIncludedDevelopmentOfSoftSkills: boolean;
  internshipIncludedEvaluation: boolean;
  otherActvitiesIncludedInInternship: string;

  studentsKnowledgeWhenStarting: number;
  qualityOfWork: number;
  qualityOfDocumentation: number;

  taskAdherence: number;
  terminologyUsage: number;
  pointsForBackground: number;
  pointsForTeamWork: number;
  pointsForResponsibility: number;
  pointsForInitiative: number;
  pointsForEffortCapacity: number;
  pointsForAdaptationCapacity: number;
  pointsForOtherSoftSkills: number;
  otherSoftSkills: string;
}

export interface EvaluationReportForComputerScience extends BaseEvaluationReport {
  // company details
  organizationMainAcitvityIsSoftwareDevelopment: boolean;
  organizationMainAcitvityIsSoftwareDevelopmentUtilization: boolean;
  organizationMainAcitvityIsITServicesAndEquipment: boolean;

  // overriding studyLineType is necessary for the type narrowing in calculation.ts
  // restricting this interface strictly to the ComputerScience type
  studyLineType: StudyLineType.ComputerScience;
  pointsForAlgorithmsAndDataStructures: number;
  pointsForProgrammingLanguagesAndEnvironments: number;
  pointsForSoftwareArchitectures: number;
  pointsForSoftwareMethodologies: number;
  pointsForDatabases: number;
  pointsForOperatingSystemsAndComputerNetworks: number;
  pointsForTesting: number;
  pointsForSecurity: number;
  pointsForOtherDevelopmentSkills: number;
  otherDevelopmentSkills: string;
}

export interface EvaluationReportForMathAndMathComputerScience extends BaseEvaluationReport {
  mainActivityForFundamentalMathematicalStructures: boolean;
  mainActivityForAlgebraAnalysisGeometryAndDifferentialEquations: boolean;
  mainActivityMathModelareMath: boolean;
  mainActivityMathProbability: boolean;
  mainActivityMathMethodologyAndPedagogy: boolean;

  studyLineType: StudyLineType.Math | StudyLineType.MathComputerScience;
  pointsForFundamentalMathematicalStructures: number;
  pointsForAlgebraAnalysisGeometryAndDifferentialEquations: number;
  pointsForMathematicalModeling: number;
  pointsForProbabilityAndStatistics: number;
  pointsForTeachingMethodologyAndDidactics: number;
  pointsForOtherMathematicalSkills: number;
  otherDevelopmentSkills: string;
}

export interface FinalGrade {
  finalGrade: number;
}

export interface ReducedDepartment {
  id: number;
  name: string;
}

export interface ReducedStudyLine {
  id: number;
  name: string;
}

export enum StudyLevel {
  Bachelor = 0,
  Master = 1,
}

export enum ResearchAgreementStatus {
  Active = 0,
  Generated = 1,
  InReview = 2,
  Rejected = 3,
  Accepted = 4,
}

export interface ResearchAgreementWithStudentName {
  studentName: string;
  organizationName: string;
  internshipProcessId: number;
  fieldOfResearch: string;
  researchTopic: string;
  descriptionOfResearch: string;
  status: ResearchAgreementStatus;
  researchAgreementId: string;
  researchAgreementDocumentUrl: string;
  tutorSigned: boolean;
  universityCoordinatorSigned: boolean;
}

export interface ResearchAgreement {
  internshipProcessId: number;
  fieldOfResearch: number;
  researchTopic: string;
  descriptionOfResearch: string;
  status: ResearchAgreementStatus;
  researchAgreementId: string;
  researchAgreementDocumentUrl: string;
  tutorSigned: boolean;
  universityCoordinatorSigned: boolean;
}

export interface ReducedResearchAgreement {
  fieldOfResearch: string;
  researchTopic: string;
  descriptionOfResearch: string;
}

export interface GroupValue {
  [key: string]: string | number | undefined;
  otherTitle?: string;
  otherValue?: number;
}

export enum ContractType {
  employed = 0,
  notEmployed = 1,
  ESF = 2, // European Social Fund
  other = 3,
}

export interface ConventionDocument {
  conventionId: number;
  registrationNumber: string;
  registrationDate: Date;
  conventionDocumentUrl: string;
  studentName: string;
  organizationName: string;
  tutorName: string;
  rectorSigned: boolean;
  isRegistered: boolean;
  studentGroup: string;
  internshipStartDate: Date;
  internshipEndDate: Date;
  // the study year of the convention is the study year of its process' session's department
  studyYearId: number;
}

export interface OrganizationsAndTutors {
  organizationName: string;
  tutorNames: Array<string>;
}

export interface DepartmentsWithSupervisors {
  departmentName: string;
  supervisorNames: Array<string>;
}

export interface StudyLinesWithSupervisors {
  studyLineName: string;
  supervisorNames: Array<string>;
  // the study year of the study line is the study year of its department
  studyYearId: number;
}

export interface SafetyDocumentInfo {
  documentUrl: string;
  studentId: number;
  studentName: string;
  studentEmail: string;
  documentState: StepStatus;
}

export interface ProcessStartSettings {
  companyRegistrationEnabled: boolean;
  internshipApplicationEnabled: boolean;
  internshipOfferCreationEnabled: boolean;
}

export interface RollbackStudent {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  // only the processes of the selected study year
  processIds: number[];
}

// one rolled back step of an internship process
export interface ProcessRollbackHistoryEntry {
  id: number;
  stepName: StepName;
  reason: string;
  rolledBackByName: string;
  // UTC, e.g. "2026-10-21T14:30:00Z"
  rolledBackAtUtc: string;
}

export interface ProcessRollback {
  stepName: StepName;
  reason: string;
  // email the student and the responsible of the step
  notify: boolean;
}

export interface IntervalValidResponse {
  // maximum hours that can be worked in the interval (0 outside a study year)
  numberOfHours: number;
  isInsideStudyYear: boolean;
  studyYearName: string | null;
  requiredHours: number | null;
  // null when no hours were sent
  isSufficient: boolean | null;
  // the hours of each sent session, by the calendar of its study line and year;
  // empty without sessions or outside the study year
  sessions: Array<SessionWorkHours>;
}

export interface SessionWorkHours {
  sessionId: number;
  studyLineName: string;
  yearOfStudy: number;
  numberOfHours: number;
  // null when no hours were sent
  isSufficient: boolean | null;
}
